import type { DescriptionPage_DifficultyLevel as DifficultyLevel } from '__generated__/graphql-types';

import _t from 'i18n!nls/unified-description-page-common';

// Values are XDP slugs.
export const GOOGLE_GATEWAYS_FOR_OVERRIDES_SLUGS = {
  GOOGLE_IT_SUPPORT: 'google-it-support',
  GOOGLE_DATA_ANALYTICS: 'google-data-analytics',
  GOOGLE_PROJECT_MANAGEMENT: 'google-project-management',
  GOOGLE_UX_DESIGN: 'google-ux-design',
  GOOGLE_IT_AUTOMATION: 'google-it-automation',
};

export const XDP_META_DESC_LENGTH_LIMIT = 160;

export const NAV_SQUARE_LOGO_SIZE = 28;

export const HERO_BACKGROUND_COLOR = 'rgba(0, 86, 210, 0.05)';

export const getXdpMetaDescriptionSuffix = () => _t('Enroll for free.');

export const humanizeDifficultyLevel = (level: DifficultyLevel) => {
  return {
    BEGINNER: _t('Beginner'),
    INTERMEDIATE: _t('Intermediate'),
    ADVANCED: _t('Advanced'),
  }[level];
};

export const CREDITS_ANCHOR_ID = 'credits';

export const PROJECTS_LEARN_ANCHOR_ID = 'learn';

export const FIXED_PAGE_NAVIGATION_Z_INDEX = 3000;

export const XDP_CHAT_PANEL_WIDTH = 416;

// SSR/pre-mount fallback only (useMeasuredCollapsedContentHeight takes over once mounted) --
// estimated by analogy from MOBILE_CHAT_TRAY_EMPTY_HEIGHT's measured composer baseline, not
// independently measured. The minimal variant's composer + one pill row footprint; consumed only
// by ConsumerHeaderFooterWrapper's footer spacer, which still reserves space for the minimal
// variant's post-scroll welcome pill row.
//
// This and the three siblings below cover drag handle + composer only. Every trace they were
// captured from predates the always-visible AI disclaimer, so callers add
// MOBILE_CHAT_TRAY_DISCLAIMER_HEIGHT on top rather than each constant being re-derived.
export const MOBILE_CHAT_TRAY_HEIGHT = 173;

// Fallback only -- see MOBILE_CHAT_TRAY_HEIGHT. Empirically measured from a live-captured trace
// (drag handle + bare composer, no header/pills) -- confirmed against real rendered output.
export const MOBILE_CHAT_TRAY_EMPTY_HEIGHT = 109;

// Fallback only -- see MOBILE_CHAT_TRAY_HEIGHT. Empirically measured from a live-captured trace
// (drag handle + heading + 2 capped pill rows + composer) -- user-confirmed as visually correct.
export const MOBILE_CHAT_TRAY_CONTEXTUAL_HEIGHT = 225;

// Fallback only -- see MOBILE_CHAT_TRAY_HEIGHT. Estimated by analogy from CONTEXTUAL_HEIGHT's
// measured baseline, not independently measured: drag handle (20) + heading (~24) + composer (89).
export const MOBILE_CHAT_TRAY_CONTEXTUAL_LOADING_HEIGHT = 133;
export const MOBILE_CHAT_TRAY_CLOSE_DRAG_THRESHOLD = 56;

// The drag handle's own rendered height (padding + handle bar); unlike composer height it never
// varies with content, so it's a constant rather than measured (see useMeasuredCollapsedContentHeight).
export const MOBILE_CHAT_TRAY_DRAG_HANDLE_HEIGHT = 20;

// Fallback only -- the disclaimer is measured live along with the composer (see
// useMeasuredCollapsedContentHeight), since its wrapped line count varies with persona and locale.
// Estimated, not trace-measured: ChatFooter's 16px padding plus ~4 wrapped bodySecondary lines.
export const MOBILE_CHAT_TRAY_DISCLAIMER_HEIGHT = 112;

// XDP Coach action IDs (MERCH-3475, MERCH-3374).
export const COACH_INTENTION_ROUTER_ACTION_ID = 'ROUTER';

// Shared by the desktop two-panel and mweb tray containers (MERCH-3536).
export const XDP_COACH_MODE_ID = 'XDP_CAREER_FIT_ASSISTANT_V3_1';

/**
 * Container query name for scoping module layout adjustments to two-panel mode.
 * Declared on TwoPanelConversationalDiscoveryLayout's content column; modules
 * within this bundle use `@container ${PAGE_CONTENT_CONTAINER_NAME} (...)` to
 * adapt to the chat-narrowed column width.
 */
export const PAGE_CONTENT_CONTAINER_NAME = 'page-content';

// Fallback when DOM measurement isn't available (page header + meta nav).
export const XDP_GLOBAL_HEADER_HEIGHT = 105;

// Ensures suggested pace (n weeks * m hrs/week) matches course's total duration.
export const CDP_SUGGESTED_NUM_WEEKS = 3;

export const MAX_NUMBER_OF_KIM_ITEMS = 4;

export const MAX_NUMBER_OF_HORIZONTAL_KIM_ITEMS = 5;

// Show only top three FAQs https://coursera.atlassian.net/browse/ACQU-1457
export const MAX_FAQS_TO_SHOW = 3;

export const getDefaultDifficultyDetail = () => ({
  get BEGINNER() {
    return _t('No prior experience required');
  },
  get INTERMEDIATE() {
    return _t('Some related experience required');
  },
  get ADVANCED() {
    return _t('Designed for those already in the industry');
  },
});

export const KIM_MOBILE_SUBTITLE_DISPLAY_OPTIONS = {
  DISPLAY_IN_MODAL: 'displayInModal',
  DISPLAY_BLOCK: 'displayBlock',
  DISPLAY_INLINE: 'displayInline',
  DISPLAY_SUBTITLE_ONLY: 'displaySubtitleOnly',
  NO_SUBTITLE: 'noSubtitle',
};

export const PROF_CERTS_USING_SKILL_CERT_LABEL = ['generative-ai-for-software-development'];

export const COURSE_NEXT_STEP_TYPE_NAME = 'courseMaterialNextStep';

export const ENROLLMENT_COUNT_THRESHOLD = 1500;
